import React, { useState } from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import { Country } from '../../graphql/pimcore/modules/geographic/types/Country';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { SalesAndResourcesContactPointsContent } from '../../graphql/pimcore/modules/siteContent/types/SalesAndResourcesContactPointsContent';

interface RepresentativeSelectProps {
    countries: Country[];
    setSelectedCountryId: (countries: string[]) => void;
    setSelectedSubnationalRegionId: (regions: string[]) => void;
    contentTree: SalesAndResourcesContactPointsContent;
}

const RepresentativeSelect: React.FC<RepresentativeSelectProps> = ({
    countries,
    setSelectedCountryId,
    setSelectedSubnationalRegionId,
    contentTree,
}) => {
    const [country, setCountry] = useState<string | null>(null);
    const [subnationalRegion, setSubnationalRegion] = useState<string | null>(null);
    const { isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } = useResponsive();

    const onCountryClick = (clickedCountryId: string) => {
        if (clickedCountryId === country) {
            setCountry(null);
            setSelectedCountryId([]);
            setSubnationalRegion(null);
            setSelectedSubnationalRegionId([]);
        } else {
            setCountry(clickedCountryId);
            setSelectedCountryId([clickedCountryId]);

            const validSubnationalRegions =
                countries
                    .find((c) => c.id === clickedCountryId)
                    ?.subnationalRegions.map((sr) => sr.id) || [];
            setSubnationalRegion(
                validSubnationalRegions.includes(subnationalRegion || '') ? subnationalRegion : null
            );
        }
    };

    const onSubnationalRegionClick = (clickedSubnationalRegionId: string) => {
        if (clickedSubnationalRegionId === subnationalRegion) {
            setSubnationalRegion(null);
            setSelectedSubnationalRegionId([]);
        } else {
            setSubnationalRegion(clickedSubnationalRegionId);
            setSelectedSubnationalRegionId([clickedSubnationalRegionId]);
        }
    };

    const selectedCountry = countries.find((c) => c.id === country);
    const subnationalRegions = selectedCountry?.subnationalRegions || [];
    const selectorWidth =
        subnationalRegions.length > 0
            ? isScreenXl || isScreenLg
                ? '435px'
                : isScreenMd
                  ? '320px'
                  : isScreenSm
                    ? '200px'
                    : isScreenXs
                      ? '140px'
                      : '585px'
            : isScreenSm
              ? '422px'
              : isScreenXs
                ? '295px'
                : '585px';
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1rem',
                paddingBottom: '1rem',
                paddingTop: '1rem',
            }}
        >
            <Box sx={{ width: selectorWidth, flexDirection: 'column', gap: '1rem' }}>
                <Typography sx={{ fontFamily: 'Poppins', marginBottom: '0.5rem' }}>
                    {contentTree.common.countrySelectPlaceholder.content}
                </Typography>
                <Select
                    fullWidth
                    value={country || ''}
                    onChange={() => {}} // Disable native change handling
                    variant="outlined"
                    displayEmpty
                    sx={{ backgroundColor: 'white' }}
                >
                    <MenuItem disabled value="">
                        <span>{contentTree.common.countrySelectLabel.content}</span>
                    </MenuItem>
                    {countries
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((option) => (
                            <MenuItem
                                key={option.id}
                                onClick={() => onCountryClick(option.id)}
                                selected={country === option.id}
                                value={option.id}
                            >
                                {option.name}
                            </MenuItem>
                        ))}
                </Select>
            </Box>

            {subnationalRegions.length > 0 && (
                <Box
                    sx={{
                        width:
                            isScreenXl || isScreenLg
                                ? '435px'
                                : isScreenMd
                                  ? '320px'
                                  : isScreenSm
                                    ? '200'
                                    : isScreenXs
                                      ? '140px'
                                      : '560px',
                        flexDirection: 'column',
                        gap: '1rem',
                    }}
                >
                    <Typography sx={{ fontFamily: 'Poppins', marginBottom: '0.5rem' }}>
                        {selectedCountry?.subnationalRegionType?.name ||
                            contentTree.common.countrySelectPlaceholder.content}
                    </Typography>
                    <Select
                        fullWidth
                        value={subnationalRegion || ''}
                        onChange={() => {}} // Disable native change handling
                        variant="outlined"
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                    >
                        <MenuItem disabled value="">
                            <span>
                                {selectedCountry?.subnationalRegionType?.selectionPrompt ||
                                    contentTree.common.subnationalRegionSelectPlaceholder.content}
                            </span>
                        </MenuItem>
                        {subnationalRegions
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((sr) => (
                                <MenuItem
                                    key={sr.id}
                                    onClick={() => onSubnationalRegionClick(sr.id)}
                                    selected={subnationalRegion === sr.id}
                                    value={sr.id}
                                >
                                    {sr.key}
                                </MenuItem>
                            ))}
                    </Select>
                </Box>
            )}
        </Box>
    );
};

export default RepresentativeSelect;
